type AppEnvironment = "local" | "staging" | "production";

const BASE_DOMAIN_BY_ENV: Record<AppEnvironment, string> = {
  local: "local.a2z-orders.shop",
  staging: "a2zorders.com",
  production: "trioty.com",
};

function getAppEnvironment(): AppEnvironment {
  const value = import.meta.env.VITE_APP_ENV;
  if (value === "local" || value === "staging" || value === "production") {
    return value;
  }

  return import.meta.env.PROD ? "production" : "local";
}

function getBaseDomain(): string {
  return BASE_DOMAIN_BY_ENV[getAppEnvironment()];
}

function ensureLeadingSlash(path?: string): string {
  if (!path || path === "/") {
    return "";
  }

  return path.startsWith("/") ? path : `/${path}`;
}

function normalizeSlug(storeSlug: string): string {
  return encodeURIComponent(storeSlug);
}

export function getBuyerHostname(storeSlug: string): string {
  const slug = normalizeSlug(storeSlug);
  const base = getBaseDomain();
  if (getAppEnvironment() === "staging") {
    return `${slug}-dev.${base}`;
  }
  return `${slug}.${base}`;
}

function getBuyerPath(locale: string, path?: string): string {
  return `/${locale}${ensureLeadingSlash(path)}`;
}

/**
 * The Trioty Restaurant staff app.
 *
 * Same apex as the seller portal on purpose (decision D1): the SPAs call the
 * API cross-origin with `credentials:"include"` and the JWT cookie is scoped to
 * the base domain, so a separate brand apex would be cross-site and the cookie
 * would simply never be sent.
 */
export function getRestaurantAppUrl(locale: string, path?: string): string {
  return `https://restaurant.${getBaseDomain()}/${locale}${ensureLeadingSlash(path)}`;
}

export function getSellerPath(
  locale: string,
  storeSlug: string,
  path?: string,
): string {
  return `/${locale}/${normalizeSlug(storeSlug)}${ensureLeadingSlash(path)}`;
}

export function getBuyerAbsoluteUrl(
  locale: string,
  storeSlug: string,
  path?: string,
): string {
  return `https://${getBuyerHostname(storeSlug)}${getBuyerPath(locale, path)}`;
}

/**
 * Public product-feed URL for the seller's Google Merchant Center / Meta
 * Commerce Manager catalog.
 *
 * Served off the store's own hostname rather than a central API domain: the
 * seller pastes this into an ad console that will fetch it for years, so it
 * should keep working if the API is ever moved behind a different host. `/api`
 * is the backend's global prefix, routed per-subdomain by the reverse proxy.
 *
 * `/products/feed.xml` is the canonical route (`PublicProductController`). The
 * shorter `/api/public/stores/<slug>/feed.xml` this used to hand out had no
 * route and 404'd; the backend now serves it too, for sellers who pasted it.
 */
export function getProductFeedUrl(storeSlug: string): string {
  const slug = normalizeSlug(storeSlug);
  return `https://${getBuyerHostname(slug)}/api/public/stores/${slug}/products/feed.xml`;
}
